import React, { useEffect, useMemo, useState } from "react";

import {
  AppPreferencesContext,
  DEFAULT_PREFERENCES,
  readStoredPreferences,
  writeStoredPreferences,
  type AppPreferences,
  type AppPreferencesContextValue
} from "@/app/preferences/appPreferencesState";

function resolveThemePreference(theme: AppPreferences["theme"]): "light" | "dark" {
  if (theme !== "system") return theme;
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function AppPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useState<AppPreferences>(() => readStoredPreferences());
  const [systemTheme, setSystemTheme] = useState<"light" | "dark">(() =>
    resolveThemePreference("system")
  );
  const resolvedTheme = preferences.theme === "system" ? systemTheme : preferences.theme;

  useEffect(() => {
    writeStoredPreferences(preferences);
  }, [preferences]);

  useEffect(() => {
    const root = document.documentElement;
    const activeTheme = resolvedTheme;
    root.classList.toggle("theme-dark", activeTheme === "dark");
    root.classList.toggle("theme-light", activeTheme === "light");
    root.dataset.theme = activeTheme;
    root.dataset.themePreference = preferences.theme;
    root.classList.toggle("prefers-high-contrast", preferences.highContrast);
    root.classList.toggle("prefers-reduced-motion-app", preferences.reducedMotion);
  }, [preferences.highContrast, preferences.reducedMotion, preferences.theme, resolvedTheme]);

  useEffect(() => {
    if (
      preferences.theme !== "system" ||
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      setSystemTheme(resolveThemePreference("system"));
    };

    mediaQuery.addEventListener?.("change", handleChange);
    return () => {
      mediaQuery.removeEventListener?.("change", handleChange);
    };
  }, [preferences.theme]);

  const value = useMemo<AppPreferencesContextValue>(
    () => ({
      preferences,
      updatePreference(key, value) {
        setPreferences((current) => ({ ...current, [key]: value }));
      },
      resetPreferences() {
        setPreferences(DEFAULT_PREFERENCES);
      }
    }),
    [preferences]
  );

  return <AppPreferencesContext.Provider value={value}>{children}</AppPreferencesContext.Provider>;
}
